/**
 * ASR Comparison Test Page
 *
 * Internal-only / dev surface. Pulls 10 random sentences with audio
 * from the user's enrolled courses, lets the tester record once, and
 * sends the same audio to OpenAI Whisper / Replicate WhisperX / Groq
 * Whisper-large-v3-turbo in parallel. Renders side-by-side: transcript
 * with WER-aligned word coloring, latency, per-request cost, plus
 * cumulative stats so a tester can run multiple sentences and see
 * which provider trends best.
 *
 * Results are cached to localStorage by segment + service so re-runs
 * accumulate; this page never writes to backend storage.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useAudioRecorder } from '../../hooks/useAudioRecorder';
import { resolveMediaUrl } from '../../utils/mediaUrl';
import { useTranslation } from 'react-i18next';
import {
  asrCompareService,
  AsrSentence,
  AsrServiceId,
  AsrWord,
  ServiceComparison,
  TranscribeResponse,
  DiffEntry,
  CoachResult,
} from '../../services/asrCompareService';
import PronunciationResultPanel from '../../components/learning/PronunciationResultPanel';
import { analyzeProsody, ProsodyAnalysis } from './utils/pitchAnalysis';
import { synthesizeFromAsr } from './utils/asrScoring';
import { detectProsodyIssues } from './utils/prosodyIssueDetector';
import { resolveIssues, resolveCoachLink } from './utils/prosodyIssueResolver';
import './AsrComparePage.css';

const PROSODY_STORAGE_KEY = 'asrCompare.prosody.v1';
const COACH_STORAGE_KEY = 'asrCompare.coach.v1';

// Azure + OpenAI Whisper both removed from the comparison fan-out:
//   - Azure: forced-aligned transcript hides errors, useless for WER.
//   - OpenAI Whisper: free-tier shared GPU pool tail-latency spikes
//     to 10s+ on ~10-15% of calls, dragging the whole 3-way fan-out.
// The two services stay registered (types / module / DTO) so flipping
// them back is a one-line restore in SERVICE_ORDER + fan-out.
const SERVICE_ORDER: AsrServiceId[] = [
  'groq-whisper',
  'deepgram',
];

const SERVICE_META: Record<AsrServiceId, { label: string; sub: string; color: string }> = {
  'openai-whisper': {
    label: 'OpenAI Whisper',
    sub: 'whisper-1 · $0.006/min',
    color: '#10a37f',
  },
  'groq-whisper': {
    label: 'Groq Whisper',
    sub: 'large-v3-turbo · $0.04/hr',
    color: '#7c3aed',
  },
  'azure-speech': {
    // Production app already uses this for pronunciation feedback —
    // the comparison surfaces what Loopy currently ships.
    label: 'Azure Speech',
    sub: 'pronunciation assessment · ~$1/hr',
    color: '#0078d4',
  },
  'deepgram': {
    label: 'Deepgram',
    sub: 'nova-2 · $0.0043/min',
    color: '#13ef93',
  },
};

interface SessionStats {
  service: AsrServiceId;
  successCount: number;
  failCount: number;
  totalLatencyMs: number;
  totalCostUsdMicros: number;
  totalWer: number;
  werCount: number;
}

const STORAGE_KEY = 'asrCompare.session.v1';

const formatCost = (microUsd?: number): string => {
  if (microUsd == null) return '—';
  // Always show USD with 6 decimals so per-call costs across services
  // (which span ~$0.0001 → $0.01) line up visually for comparison.
  // The earlier "m" milli-USD switch made tiny costs look like a
  // different unit and broke the at-a-glance comparison.
  return `$${(microUsd / 1_000_000).toFixed(6)}`;
};

const formatLatency = (ms: number): string => {
  if (ms < 1000) return `${ms} ms`;
  return `${(ms / 1000).toFixed(2)} s`;
};

const colorForOp = (op: DiffEntry['op']): string => {
  switch (op) {
    case 'match': return '#16a34a';
    case 'substitute': return '#ca8a04';
    case 'delete': return '#dc2626';
    case 'insert': return '#dc2626';
  }
};

const labelForOp = (op: DiffEntry['op']): string => {
  switch (op) {
    case 'match': return '對';
    case 'substitute': return '錯';
    case 'delete': return '漏';
    case 'insert': return '多';
  }
};

const AsrComparePage: React.FC = () => {
  const [sentences, setSentences] = useState<AsrSentence[]>([]);
  const [loading, setLoading] = useState(false);
  const [activeIdx, setActiveIdx] = useState<number | null>(null);
  const [transcribing, setTranscribing] = useState(false);
  const [results, setResults] = useState<Record<string, TranscribeResponse>>(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : {};
    } catch {
      return {};
    }
  });
  const [prosodyByseg, setProsodyByseg] = useState<Record<string, ProsodyAnalysis>>(() => {
    try {
      const raw = localStorage.getItem(PROSODY_STORAGE_KEY);
      return raw ? JSON.parse(raw) : {};
    } catch {
      return {};
    }
  });
  const [coachByseg, setCoachByseg] = useState<Record<string, CoachResult>>(() => {
    try {
      const raw = localStorage.getItem(COACH_STORAGE_KEY);
      return raw ? JSON.parse(raw) : {};
    } catch {
      return {};
    }
  });
  const [coachLoading, setCoachLoading] = useState<Record<string, boolean>>({});
  const [error, setError] = useState<string | null>(null);
  const { t: tProsodyRef } = useTranslation('prosodyRef');

  // Disable browser audio processing for the comparison page only —
  // Chrome's default `noiseSuppression: true` actively kills the
  // turbulent component of voiced speech, which then makes YIN
  // unable to find clean periodicity in genuinely-voiced frames.
  // Production speaking surfaces keep these on (better speech-to-text
  // accuracy); the comparison page wants the raw mic signal so the
  // synthesised prosody score reflects the speaker, not the
  // pre-processor.
  const { isRecording, audioBlob, audioUrl, startRecording, stopRecording, clearRecording } =
    useAudioRecorder({
      audioConstraints: {
        echoCancellation: false,
        noiseSuppression: false,
        autoGainControl: false,
      },
    });

  const audioRef = useRef<HTMLAudioElement | null>(null);

  // Persist results across reloads so the tester can build up a sample.
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(results));
  }, [results]);

  useEffect(() => {
    localStorage.setItem(PROSODY_STORAGE_KEY, JSON.stringify(prosodyByseg));
  }, [prosodyByseg]);

  useEffect(() => {
    localStorage.setItem(COACH_STORAGE_KEY, JSON.stringify(coachByseg));
  }, [coachByseg]);

  const loadSentences = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await asrCompareService.getSentences();
      setSentences(res.sentences);
      if (res.sentences.length === 0) {
        setError('No sentences found in your enrolled courses with audio.');
      }
    } catch (e) {
      setError((e as Error).message || 'Failed to load sentences');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSentences();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const activeSentence = activeIdx != null ? sentences[activeIdx] : null;

  const blobToBase64 = (blob: Blob): Promise<string> =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const dataUrl = reader.result as string;
        resolve(dataUrl.split(',')[1] ?? dataUrl);
      };
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });

  // Measure audio duration via the browser. Bypasses the round-trip-
  // latency proxy the Azure backend was using for cost estimation.
  const measureDurationSec = (blob: Blob): Promise<number | undefined> =>
    new Promise(resolve => {
      const url = URL.createObjectURL(blob);
      const audio = new Audio();
      audio.preload = 'metadata';
      const cleanup = () => URL.revokeObjectURL(url);
      audio.onloadedmetadata = () => {
        const duration = Number.isFinite(audio.duration) ? audio.duration : undefined;
        cleanup();
        resolve(duration);
      };
      audio.onerror = () => { cleanup(); resolve(undefined); };
      audio.src = url;
      // Some browsers report Infinity for WebM until metadata is forced.
      // Setting currentTime to a large value coerces a real duration read.
      window.setTimeout(() => {
        if (!Number.isFinite(audio.duration) || audio.duration === 0) {
          audio.currentTime = 1e6;
        }
      }, 100);
    });

  const submitTranscription = async () => {
    if (!audioBlob || !activeSentence) return;
    const segmentId = activeSentence.segmentId;
    const referenceText = activeSentence.text;
    const blob = audioBlob;
    setTranscribing(true);
    setError(null);

    // Run ASR (network) and prosody (~1-2s of pure JS via YIN) as
    // independent tracks so the "送辨識中" button releases the
    // moment ASR returns, instead of waiting for the YIN pass to
    // finish too. Coach call kicks off only when both tracks are
    // home — it needs the diff + the F0 contour.
    let prosodyResult: ProsodyAnalysis | null = null;
    let asrResult: TranscribeResponse | null = null;

    const tryFireCoach = (): void => {
      if (!asrResult || !prosodyResult) return;
      const preferred: AsrServiceId[] = ['deepgram', 'groq-whisper', 'openai-whisper'];
      const driver = asrResult.services.find(s =>
        s.result.ok
        && s.diff
        && preferred.includes(s.result.service),
      ) ?? asrResult.services.find(s => s.result.ok && s.diff);
      if (driver && driver.diff) {
        void requestCoaching(segmentId, driver, prosodyResult, asrResult.referenceText);
      }
    };

    try {
      const [base64, durationSec] = await Promise.all([
        blobToBase64(blob),
        measureDurationSec(blob),
      ]);

      // Track 1 (network): ASR fan-out. Releases the submit button
      // and renders all four cards as soon as it returns.
      const asrTask = asrCompareService
        .transcribeAll(
          base64,
          blob.type || 'audio/webm',
          referenceText,
          durationSec,
        )
        .then(res => {
          asrResult = res;
          setResults(prev => ({ ...prev, [segmentId]: res }));
          setTranscribing(false);
          tryFireCoach();
          return res;
        });

      // Track 2 (CPU): YIN prosody analysis. Updates the synthesised
      // panel + contour when done — independent of ASR's wall-clock.
      const prosodyTask = analyzeProsody(blob)
        .then(p => {
          prosodyResult = p;
          setProsodyByseg(prev => ({ ...prev, [segmentId]: p }));
          tryFireCoach();
          return p;
        })
        .catch(err => {
          console.warn('[asr-compare] prosody analysis failed', err);
          return null;
        });

      await asrTask;
      // We've already released the button; prosody track is allowed
      // to finish in the background. Catch its rejection here so we
      // don't leave an unhandled promise.
      prosodyTask.catch(() => undefined);
    } catch (e) {
      setError((e as Error).message || 'Transcription failed');
      setTranscribing(false);
    }
  };

  const requestCoaching = async (
    segmentId: string,
    driver: ServiceComparison,
    prosody: ProsodyAnalysis,
    referenceText: string,
  ): Promise<void> => {
    if (!driver.diff || !driver.result.ok) return;
    setCoachLoading(prev => ({ ...prev, [segmentId]: true }));
    try {
      const issues = detectProsodyIssues({
        prosody,
        asr: driver.result,
        diff: driver.diff,
        referenceText,
      });
      const resolved = resolveIssues(issues, tProsodyRef);
      const coach = await asrCompareService.coach({
        referenceText,
        transcript: driver.result.transcript,
        prosody: {
          score: prosody.score,
          pitchRangeSt: prosody.pitchRangeSt,
          pitchVarianceSt: prosody.pitchVarianceSt,
          voicedRatio: prosody.voicedRatio,
        },
        issues: resolved.map(r => ({
          category: r.category,
          severity: r.severity,
          title: r.title,
          evidenceLine: r.evidenceLine,
          refKey: r.refKey,
          refEntry: r.refEntry,
        })),
      });
      setCoachByseg(prev => ({ ...prev, [segmentId]: coach }));
    } catch (err) {
      console.warn('[asr-compare] coaching failed', err);
    } finally {
      setCoachLoading(prev => ({ ...prev, [segmentId]: false }));
    }
  };

  const stats = useMemo<SessionStats[]>(() => {
    const byService: Record<AsrServiceId, SessionStats> = {
      'openai-whisper': emptyStats('openai-whisper'),
      'groq-whisper': emptyStats('groq-whisper'),
      'azure-speech': emptyStats('azure-speech'),
      'deepgram': emptyStats('deepgram'),
    };
    Object.values(results).forEach(r =>
      r.services.forEach(c => {
        // Skip orphaned services from older sessions (e.g.
        // replicate-whisperx left in localStorage from before the
        // Azure swap). Without this guard the stale entry crashes
        // the stats reducer.
        const s = byService[c.result.service];
        if (!s) return;
        if (c.result.ok) s.successCount++;
        else s.failCount++;
        s.totalLatencyMs += c.result.latencyMs;
        if (c.result.costUsdMicros) s.totalCostUsdMicros += c.result.costUsdMicros;
        if (c.diff) {
          s.totalWer += c.diff.wer;
          s.werCount++;
        }
      }),
    );
    return SERVICE_ORDER.map(id => byService[id]);
  }, [results]);

  const playReference = (url: string) => {
    if (!audioRef.current) audioRef.current = new Audio();
    const audio = audioRef.current;
    audio.src = resolveMediaUrl(url);
    audio.play().catch(() => {/* ignore autoplay policy */});
  };

  return (
    <div className="asr-compare-page">
      <header className="asr-compare-header">
        <h1>ASR 轉錄比較</h1>
        <p className="asr-compare-subtitle">
          抽 10 句課文，同一份音檔送四家：OpenAI Whisper / Groq / Azure Speech / Deepgram。比較 transcript / WER / 延遲 / 成本。
          {' '}非 Azure 三家會額外套上「<strong>合成多軸</strong>」面板：accuracy/fluency/completeness 來自 diff + 字級時間戳記，prosody 來自瀏覽器端 YIN F0 分析（無 API 成本）。
          {' '}<strong>注意</strong>：Azure pronunciation-assessment 會 forced-align 到 reference，WER 會被低估。
        </p>
        <div className="asr-compare-actions">
          <button onClick={loadSentences} disabled={loading} className="asr-compare-btn">
            重新抽 10 句
          </button>
          <button
            onClick={() => {
              setResults({});
              setProsodyByseg({});
              setCoachByseg({});
              localStorage.removeItem(STORAGE_KEY);
              localStorage.removeItem(PROSODY_STORAGE_KEY);
              localStorage.removeItem(COACH_STORAGE_KEY);
            }}
            className="asr-compare-btn asr-compare-btn--ghost"
          >
            清除累積結果
          </button>
        </div>
      </header>

      {error && <div className="asr-compare-error">{error}</div>}

      <div className="asr-compare-layout">
        <aside className="asr-compare-sentences">
          <h2>句子（{sentences.length}）</h2>
          {sentences.map((s, idx) => (
            <button
              key={s.segmentId}
              onClick={() => {
                setActiveIdx(idx);
                clearRecording();
              }}
              className={`asr-compare-sentence-row${idx === activeIdx ? ' active' : ''}`}
            >
              <div className="asr-compare-sentence-meta">
                <span className="asr-compare-course">{s.courseTitle}</span>
                {results[s.segmentId] && (
                  <span className="asr-compare-done-badge">已測</span>
                )}
              </div>
              <div className="asr-compare-sentence-text">{s.text}</div>
            </button>
          ))}
        </aside>

        <main className="asr-compare-main">
          {!activeSentence ? (
            <div className="asr-compare-empty">← 從左邊選一句開始</div>
          ) : (
            <>
              <section className="asr-compare-active-sentence">
                <div className="asr-compare-active-header">
                  <span className="asr-compare-active-course">
                    {activeSentence.courseTitle} · #{activeSentence.sentenceIndex}
                  </span>
                  <button
                    className="asr-compare-btn asr-compare-btn--ghost"
                    onClick={() => playReference(activeSentence.audioUrl)}
                  >
                    🔊 播放原音
                  </button>
                </div>
                <h2 className="asr-compare-active-text">{activeSentence.text}</h2>
                {activeSentence.translation && (
                  <p className="asr-compare-active-translation">{activeSentence.translation}</p>
                )}
              </section>

              <section className="asr-compare-record">
                <div className="asr-compare-record-row">
                  {!isRecording ? (
                    <button
                      onClick={() => {
                        clearRecording();
                        startRecording();
                      }}
                      disabled={transcribing}
                      className="asr-compare-record-btn"
                    >
                      🎙️ 開始錄音
                    </button>
                  ) : (
                    <button onClick={stopRecording} className="asr-compare-record-btn recording">
                      ⏹ 停止錄音
                    </button>
                  )}
                  {audioBlob && audioUrl && (
                    <>
                      <audio src={audioUrl} controls className="asr-compare-record-audio" />
                      <button
                        onClick={submitTranscription}
                        disabled={transcribing}
                        className="asr-compare-btn asr-compare-btn--primary"
                      >
                        {transcribing ? '送辨識中…' : '送辨識（4 家）'}
                      </button>
                    </>
                  )}
                </div>
              </section>

              {/* Loopy coaching block — Gemini-generated 1
                  encouragement + 1-3 tips for this recording.
                  Sits above the per-service detail cards so the
                  high-level takeaway lands first. */}
              {results[activeSentence.segmentId] && (
                <CoachingPanel
                  loading={!!coachLoading[activeSentence.segmentId]}
                  result={coachByseg[activeSentence.segmentId]}
                />
              )}

              <section className="asr-compare-results">
                {results[activeSentence.segmentId] ? (
                  <ResultsGrid
                    response={results[activeSentence.segmentId]}
                    prosody={prosodyByseg[activeSentence.segmentId]}
                  />
                ) : (
                  <div className="asr-compare-empty">錄音 → 點「送辨識」看結果</div>
                )}
              </section>
            </>
          )}

          <section className="asr-compare-stats">
            <h3>累積統計（{Object.keys(results).length} 句）</h3>
            <table className="asr-compare-stats-table">
              <thead>
                <tr>
                  <th>Service</th>
                  <th>成功 / 失敗</th>
                  <th>平均 latency</th>
                  <th>平均 WER</th>
                  <th>累計成本</th>
                </tr>
              </thead>
              <tbody>
                {stats.map(s => {
                  const total = s.successCount + s.failCount;
                  const avgLatency = total > 0 ? s.totalLatencyMs / total : 0;
                  const avgWer = s.werCount > 0 ? s.totalWer / s.werCount : 0;
                  return (
                    <tr key={s.service}>
                      <td>
                        <span style={{ color: SERVICE_META[s.service].color, fontWeight: 600 }}>
                          {SERVICE_META[s.service].label}
                        </span>
                      </td>
                      <td>{s.successCount} / {s.failCount}</td>
                      <td>{formatLatency(Math.round(avgLatency))}</td>
                      <td>{(avgWer * 100).toFixed(1)}%</td>
                      <td>{formatCost(s.totalCostUsdMicros)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </section>
        </main>
      </div>
    </div>
  );
};

const emptyStats = (service: AsrServiceId): SessionStats => ({
  service,
  successCount: 0,
  failCount: 0,
  totalLatencyMs: 0,
  totalCostUsdMicros: 0,
  totalWer: 0,
  werCount: 0,
});

const ResultsGrid: React.FC<{
  response: TranscribeResponse;
  prosody?: ProsodyAnalysis;
}> = ({ response, prosody }) => {
  const byService: Partial<Record<AsrServiceId, ServiceComparison>> = {};
  response.services.forEach(s => { byService[s.result.service] = s; });

  return (
    <div className="asr-compare-results-grid">
      {SERVICE_ORDER.map(id => {
        const c = byService[id];
        const meta = SERVICE_META[id];
        if (!c) return null;
        return (
          <div key={id} className="asr-compare-result-card">
            <header className="asr-compare-result-header" style={{ borderColor: meta.color }}>
              <div>
                <h3 style={{ color: meta.color }}>{meta.label}</h3>
                <span className="asr-compare-result-sub">{meta.sub}</span>
              </div>
              <div className="asr-compare-result-meta">
                <span>{formatLatency(c.result.latencyMs)}</span>
                <span>{formatCost(c.result.costUsdMicros)}</span>
              </div>
            </header>

            {!c.result.ok ? (
              <div className="asr-compare-result-error">
                <strong>失敗</strong> {c.result.error}
              </div>
            ) : (
              <>
                {c.diff && (
                  <div className="asr-compare-wer">
                    WER {(c.diff.wer * 100).toFixed(1)}% · 對 {c.diff.matches} ·
                    錯 {c.diff.substitutions} · 漏 {c.diff.deletions} · 多 {c.diff.insertions}
                  </div>
                )}
                <div className="asr-compare-transcript">
                  {c.diff
                    ? c.diff.entries.map((e, idx) => (
                        <span
                          key={idx}
                          className={`asr-compare-token op-${e.op}`}
                          style={{ color: colorForOp(e.op) }}
                          title={`${labelForOp(e.op)}${e.ref ? ` · 原: ${e.ref}` : ''}${e.hyp ? ` · 聽: ${e.hyp}` : ''}`}
                        >
                          {e.hyp || e.ref}
                          {e.op === 'substitute' && e.ref && (
                            <em className="asr-compare-ref-hint">→{e.ref}</em>
                          )}
                        </span>
                      ))
                    : <span>{c.result.transcript}</span>}
                </div>
                {/* Azure-only: show the multi-axis pronunciation
                    panel that production already uses. Whisper / Groq
                    are pure ASR so they don't have these scores —
                    the gap is the point of the comparison. */}
                {c.result.azureAssessment && (
                  <div className="asr-compare-azure-extra">
                    <h4>Azure 多軸評分</h4>
                    <PronunciationResultPanel
                      result={{
                        overallScore: c.result.azureAssessment.overallScore,
                        accuracyScore: c.result.azureAssessment.accuracyScore,
                        fluencyScore: c.result.azureAssessment.fluencyScore,
                        completenessScore: c.result.azureAssessment.completenessScore,
                        prosodyScore: c.result.azureAssessment.prosodyScore,
                        words: c.result.azureAssessment.words,
                      }}
                    />
                  </div>
                )}

                {/* Synthesised multi-axis: built from this card's diff
                    + word timestamps + the WebAudio prosody score.
                    Surfaced on every non-Azure service so we can see
                    how each ASR's transcript quality feeds into the
                    same accuracy/fluency/completeness math, sharing a
                    common prosody score from the same recording. */}
                {!c.result.azureAssessment && c.diff && c.result.ok && prosody && (
                  <div className="asr-compare-synth-extra">
                    <h4>合成多軸 · {SERVICE_META[id].label} + WebAudio</h4>
                    <PronunciationResultPanel
                      result={synthesizeFromAsr({
                        diff: c.diff,
                        asr: c.result,
                        referenceText: response.referenceText,
                        prosody: {
                          score: prosody.score,
                          pitchRangeSt: prosody.pitchRangeSt,
                          pitchVarianceSt: prosody.pitchVarianceSt,
                          voicedRatio: prosody.voicedRatio,
                        },
                      })}
                    />
                    <ProsodyBreakdown prosody={prosody} />
                    {c.result.words && c.result.words.length > 0 && (
                      <ProsodyContour prosody={prosody} words={c.result.words} />
                    )}
                    {c.result.words?.some(w => w.confidence != null) && (
                      <div className="asr-compare-synth-debug">
                        ※ accuracy 已用 per-word confidence 加權 (Deepgram)
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        );
      })}
    </div>
  );
};

/**
 * Show how the 0-100 prosody score decomposes into the three
 * underlying signals — so a 52 doesn't read as a black box. Mirrors
 * the formula in pitchAnalysis.ts:
 *   score = 40·(range / 8) + 35·(variance / 2.5) + 25·(voiced / 0.5)
 * (each component clamped to [0,1] before weighting)
 *
 * Each row also surfaces an actionable hint when the signal is below
 * 60% of its target — these are the failure modes most learners hit.
 */
const ProsodyBreakdown: React.FC<{ prosody: ProsodyAnalysis }> = ({ prosody }) => {
  const rangeRatio = Math.min(prosody.pitchRangeSt / 8, 1);
  const varianceRatio = Math.min(prosody.pitchVarianceSt / 2.5, 1);
  const voicedRatio = Math.min(prosody.voicedRatio / 0.65, 1);
  const rangePts = Math.round(40 * rangeRatio);
  const variancePts = Math.round(35 * varianceRatio);
  const voicedPts = Math.round(25 * voicedRatio);

  type Row = {
    label: string;
    explainer: string;
    value: string;
    pts: number;
    max: number;
    hint?: string;
  };
  const rows: Row[] = [
    {
      label: '音高起伏',
      explainer: '整句最高音 vs 最低音的差距',
      value: `${prosody.pitchRangeSt.toFixed(1)} 個半音 / 目標 8`,
      pts: rangePts,
      max: 40,
      hint: rangeRatio < 0.6
        ? '太單調 — 在重要的字（名詞、動詞）上把音調拉高一點'
        : undefined,
    },
    {
      label: '音調變化',
      explainer: '整段音高的標準差，反映「動感」',
      value: `${prosody.pitchVarianceSt.toFixed(1)} 個半音 / 目標 2.5`,
      pts: variancePts,
      max: 35,
      hint: varianceRatio < 0.6
        ? '音調太平 — 把句尾陳述語調降下來、疑問句尾上揚'
        : undefined,
    },
    {
      label: '聲帶振動',
      explainer: '在你「真的有出聲」的 frame 裡，幾成是有清楚音高的（母音、m/n/l/r 等濁音）',
      value: `${(prosody.voicedRatio * 100).toFixed(0)}% / 目標 65%`,
      pts: voicedPts,
      max: 25,
      hint: voicedRatio < 0.6
        ? '濁音比例偏低 — 可能是發音偏氣音、字尾母音吞掉、或錄音背景噪音太大'
        : undefined,
    },
  ];

  const colorFor = (ratio: number): string =>
    ratio >= 0.8 ? '#16a34a' : ratio >= 0.5 ? '#ca8a04' : '#dc2626';

  return (
    <div className="asr-compare-prosody-breakdown">
      <div className="asr-compare-prosody-breakdown-title">
        韻律 {prosody.score} 分 = 三項加總
      </div>
      {rows.map(r => {
        const ratio = r.pts / r.max;
        return (
          <div key={r.label} className="asr-compare-prosody-row">
            <div className="asr-compare-prosody-main">
              <span className="asr-compare-prosody-label">{r.label}</span>
              <span className="asr-compare-prosody-value">{r.value}</span>
              <span className="asr-compare-prosody-pts" style={{ color: colorFor(ratio) }}>
                {r.pts}/{r.max}
              </span>
            </div>
            <div className="asr-compare-prosody-explainer">{r.explainer}</div>
            {r.hint && (
              <div className="asr-compare-prosody-hint">→ {r.hint}</div>
            )}
          </div>
        );
      })}
    </div>
  );
};

/**
 * Loopy 教練建議 — Gemini-generated encouragement + 1-3 tips for the
 * current recording. Driven by the client-side detector + resolver,
 * then handed to the server for natural-language wrap-up. Detection
 * is deterministic so we can debug the reasoning chain (detector
 * emits → resolver pairs with prosodyRef / phonicsRef → Gemini
 * phrases) without prompt-replaying.
 *
 * Every detected category now has a "看詳細解說 →" deep-link:
 *   - mispronunciation → /knowledge/phonics
 *   - everything else  → /knowledge/prosody#<refKey>
 * Routing centralised in resolveCoachLink so the prompt and the UI
 * use the same mapping table.
 */
const CoachingPanel: React.FC<{
  loading: boolean;
  result?: CoachResult;
}> = ({ loading, result }) => {
  if (loading && !result) {
    return (
      <section className="asr-compare-coach asr-compare-coach--loading">
        <div className="asr-compare-coach-title">Loopy 教練</div>
        <div className="asr-compare-coach-loading">
          正在分析你的錄音 ⋯
        </div>
      </section>
    );
  }
  if (!result) return null;
  return (
    <section className="asr-compare-coach">
      <div className="asr-compare-coach-title">Loopy 教練</div>
      {result.encouragement && (
        <p className="asr-compare-coach-encouragement">{result.encouragement}</p>
      )}
      {result.tips.length > 0 && (
        <ul className="asr-compare-coach-tips">
          {result.tips.map((tip, idx) => {
            const link = resolveCoachLink(tip.issue);
            return (
              <li key={`${tip.issue}-${idx}`}>
                <span className="asr-compare-coach-tip-marker">{idx + 1}</span>
                <span className="asr-compare-coach-tip-body">
                  {tip.advice}
                  {link && (
                    <>
                      {' '}
                      <a
                        href={link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="asr-compare-coach-tip-link"
                      >
                        看詳細解說 →
                      </a>
                    </>
                  )}
                </span>
                <span className="asr-compare-coach-tip-tag">#{tip.issue}</span>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
};

/**
 * Visualise *where* the prosody score came from. The breakdown above
 * answers "which axis is dragging the score down"; this answers
 * "which moment in the recording is the problem".
 *
 *  - F0 contour line shows pitch trajectory; a near-horizontal line
 *    is a monotone read.
 *  - Highest + lowest pitch points marked with semitone offsets, so
 *    "音高起伏 4.7 st" is grounded in two visible points.
 *  - Each word label is tinted by its voiced-frame ratio: green when
 *    the word came out cleanly voiced, red when it was mostly
 *    aspirated / dropped — addresses "聲帶振動 39%" by pointing at
 *    the specific words causing it.
 */
const ProsodyContour: React.FC<{
  prosody: ProsodyAnalysis;
  words: AsrWord[];
}> = ({ prosody, words }) => {
  const voicedHz = prosody.f0Frames.filter((f): f is number => f !== null);
  if (voicedHz.length < 5 || words.length === 0) return null;

  // Use median F0 as the semitone reference so the y-scale is
  // speaker-independent (different fundamentals shouldn't shift the
  // shape).
  const sortedHz = [...voicedHz].sort((a, b) => a - b);
  const medianHz = sortedHz[Math.floor(sortedHz.length / 2)];
  const toSt = (hz: number) => 12 * Math.log2(hz / medianHz);

  const allSt = voicedHz.map(toSt).sort((a, b) => a - b);
  const minSt = allSt[Math.floor(allSt.length * 0.02)] - 0.5;
  const maxSt = allSt[Math.floor(allSt.length * 0.98)] + 0.5;

  // Time window from first to last word — keeps the visual focused
  // on the speech region, not the leading silence.
  const xStart = Math.max(0, words[0].start ?? prosody.frameTimes[0] ?? 0);
  const xEnd = words[words.length - 1].end
    ?? prosody.frameTimes[prosody.frameTimes.length - 1]
    ?? xStart + 1;
  const xRange = Math.max(xEnd - xStart, 0.001);

  const W = 600;
  const H = 140;
  const padX = 8;
  const padTop = 22;
  const padBottom = 36;
  const contourH = H - padTop - padBottom;

  const xFor = (t: number): number => padX + ((t - xStart) / xRange) * (W - 2 * padX);
  const yFor = (st: number): number =>
    padTop + (1 - (st - minSt) / Math.max(maxSt - minSt, 0.01)) * contourH;

  // Build the contour line, breaking at unvoiced gaps so we don't
  // draw straight lines across silence.
  let path = '';
  let inSegment = false;
  prosody.f0Frames.forEach((f, i) => {
    const t = prosody.frameTimes[i];
    if (t < xStart || t > xEnd) return;
    if (f === null) {
      inSegment = false;
      return;
    }
    const x = xFor(t).toFixed(1);
    const y = yFor(toSt(f)).toFixed(1);
    path += inSegment ? `L${x},${y} ` : `M${x},${y} `;
    inSegment = true;
  });

  // Find peak + trough among in-window voiced frames.
  let peak = { hz: 0, t: 0 };
  let trough = { hz: Infinity, t: 0 };
  prosody.f0Frames.forEach((f, i) => {
    if (f === null) return;
    const t = prosody.frameTimes[i];
    if (t < xStart || t > xEnd) return;
    if (f > peak.hz) peak = { hz: f, t };
    if (f < trough.hz) trough = { hz: f, t };
  });
  const hasPeakTrough = peak.hz > 0 && trough.hz < Infinity;

  // Per-word voiced ratio. ASR word timestamps typically carry ±30-50ms
  // error mid-sentence, but the *first* and *last* word can be
  // clipped much harder — Deepgram tends to mark the last word's
  // end at the consonant burst and miss the trailing vowel.
  // Mid-sentence padding is ±50ms; edge words extend up to 500ms
  // toward the audio boundary (still bounded by the neighbour so we
  // don't double-count an interior pause).
  const VOICING_PAD_SEC = 0.05;
  const EDGE_PAD_SEC = 0.5;
  const wordVoiced = (w: AsrWord, idx: number): number | null => {
    const ws = w.start, we = w.end;
    if (ws == null || we == null) return null;
    const isFirst = idx === 0;
    const isLast = idx === words.length - 1;
    const leftPad = isFirst ? EDGE_PAD_SEC : VOICING_PAD_SEC;
    const rightPad = isLast ? EDGE_PAD_SEC : VOICING_PAD_SEC;
    let wsPad = ws - leftPad;
    let wePad = we + rightPad;
    if (idx > 0 && words[idx - 1].end != null) {
      wsPad = Math.max(wsPad, words[idx - 1].end! - VOICING_PAD_SEC);
    }
    if (idx < words.length - 1 && words[idx + 1].start != null) {
      wePad = Math.min(wePad, words[idx + 1].start! + VOICING_PAD_SEC);
    }
    let total = 0, voiced = 0;
    for (let i = 0; i < prosody.frameTimes.length; i++) {
      const t = prosody.frameTimes[i];
      if (t < wsPad || t >= wePad) continue;
      total++;
      if (prosody.f0Frames[i] !== null) voiced++;
    }
    return total > 0 ? voiced / total : null;
  };
  const wordColor = (ratio: number | null): string => {
    if (ratio == null) return '#94a3b8';
    if (ratio >= 0.6) return '#16a34a';
    if (ratio >= 0.3) return '#ca8a04';
    return '#dc2626';
  };

  return (
    <div className="asr-compare-prosody-contour">
      <div className="asr-compare-prosody-contour-title">音高曲線（標出最高、最低音的位置）</div>
      <svg
        viewBox={`0 0 ${W} ${H}`}
        preserveAspectRatio="none"
        className="asr-compare-prosody-svg"
      >
        {/* Median reference line — anchor for "where neutral pitch sits" */}
        <line
          x1={padX} y1={yFor(0)} x2={W - padX} y2={yFor(0)}
          stroke="#cbd5e1" strokeWidth={1} strokeDasharray="2 3"
        />

        {/* Word boundary verticals */}
        {words.map((w, i) => {
          if (w.start == null || w.end == null) return null;
          const x = xFor(w.start);
          if (x < padX || x > W - padX) return null;
          return (
            <line
              key={`b-${i}`}
              x1={x} y1={padTop} x2={x} y2={H - padBottom}
              stroke="#f1f5f9" strokeWidth={1}
            />
          );
        })}

        {/* "Active but unvoiced" frames — frame had energy above
            the silence gate, but YIN couldn't find clean periodicity.
            Silent frames (pauses) are skipped so the dots only flag
            real candidates for false-negative voicing. */}
        {prosody.f0Frames.map((f, i) => {
          if (f !== null) return null;
          if (prosody.silent[i]) return null;
          const t = prosody.frameTimes[i];
          if (t < xStart || t > xEnd) return null;
          return (
            <circle
              key={`u-${i}`}
              cx={xFor(t)} cy={H - padBottom + 4}
              r={1.4} fill="#94a3b8" fillOpacity={0.7}
            />
          );
        })}

        {/* Contour line itself */}
        <path d={path} fill="none" stroke="#667eea" strokeWidth={1.6} strokeLinejoin="round" />

        {/* Peak + trough markers */}
        {hasPeakTrough && (
          <>
            <circle cx={xFor(peak.t)} cy={yFor(toSt(peak.hz))} r={4} fill="#dc2626" />
            <text
              x={xFor(peak.t)} y={yFor(toSt(peak.hz)) - 7}
              fontSize={10} fill="#dc2626" textAnchor="middle" fontWeight={600}
            >
              ↑ {toSt(peak.hz).toFixed(1)} st
            </text>
            <circle cx={xFor(trough.t)} cy={yFor(toSt(trough.hz))} r={4} fill="#0ea5e9" />
            <text
              x={xFor(trough.t)} y={yFor(toSt(trough.hz)) + 14}
              fontSize={10} fill="#0ea5e9" textAnchor="middle" fontWeight={600}
            >
              ↓ {toSt(trough.hz).toFixed(1)} st
            </text>
          </>
        )}

        {/* Word labels under the time axis, tinted by voiced ratio */}
        {words.map((w, i) => {
          if (w.start == null || w.end == null) return null;
          const cx = xFor((w.start + w.end) / 2);
          if (cx < padX || cx > W - padX) return null;
          const ratio = wordVoiced(w, i);
          return (
            <text
              key={`w-${i}`}
              x={cx} y={H - 14}
              fontSize={11} textAnchor="middle"
              fill={wordColor(ratio)}
              fontWeight={ratio != null && ratio < 0.3 ? 700 : 500}
            >
              {w.word}
            </text>
          );
        })}

        {/* Secondary line: per-word voiced % under each word */}
        {words.map((w, i) => {
          if (w.start == null || w.end == null) return null;
          const cx = xFor((w.start + w.end) / 2);
          if (cx < padX || cx > W - padX) return null;
          const ratio = wordVoiced(w, i);
          if (ratio == null) return null;
          return (
            <text
              key={`p-${i}`}
              x={cx} y={H - 2}
              fontSize={9} textAnchor="middle" fill="#94a3b8"
            >
              {Math.round(ratio * 100)}%
            </text>
          );
        })}
      </svg>
      <div className="asr-compare-prosody-contour-legend">
        <span><span className="dot" style={{ background: '#16a34a' }} />濁音穩</span>
        <span><span className="dot" style={{ background: '#ca8a04' }} />偏氣音</span>
        <span><span className="dot" style={{ background: '#dc2626' }} />多氣音／缺音高</span>
        <span><span className="dot" style={{ background: '#dc2626' }} />↑ 最高音</span>
        <span><span className="dot" style={{ background: '#0ea5e9' }} />↓ 最低音</span>
        <span><span className="dot" style={{ background: '#94a3b8' }} />底部灰點：YIN 沒鎖到 F0 的 frame</span>
      </div>
    </div>
  );
};

export default AsrComparePage;
